// Iter170 · Vista Competencia: distribución de winner/incumbent por año y provincia
// Iter173 · Refactor:
//   • Nombres de operadores normalizados en backend (FCC Medio Ambiente == FCC MEDIO AMBIENTE).
//   • Porcentajes fieles (V06): cada % = adjudicaciones del operador ÷ total del año,
//     exacto a un decimal. Pueden no sumar 100 por redondeo (se declara, no se fuerza).
//     "Otros" agrega la cola fuera del top; "Pendiente / Sin winner" el resto no atribuido.
//   • Tres indicadores separados:
//       - Resuelto en el año (por fecha_adjudicacion; sin pendientes de adjudicar).
//       - Presentado en el año (por fecha_presentacion; incluye pendientes → provisional).
//       - Incumbents (histórico).
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from "recharts";
import { Trophy, MapPin, Loader2, TrendingUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import { ALL_ACTIVIDADES } from "./shared";

// Paleta consistente para operadores. Reservamos gris para "Otros" y ámbar para "Pendiente".
const OPERATOR_COLORS = ["#059669", "#dc2626", "#2563eb", "#d97706", "#7c3aed", "#0891b2", "#db2777", "#ca8a04", "#16a34a", "#e11d48"];
const OTROS_COLOR = "#9ca3af";
const PENDIENTE_COLOR = "#f59e0b";

// Iter178 · Paleta fija por actividad para el mini donut (misma en todas las filas)
const ACTIVITY_COLORS = {
  "C&W": "#059669", "GA": "#0891b2", "MOB": "#d97706",
  "Light Capex": "#7c3aed", "PPP": "#db2777", "O&M": "#ca8a04",
  "INDUSTRIAL": "#e11d48", "Facility Services": "#16a34a",
};
const activityColor = (act) => ACTIVITY_COLORS[act] || "#94a3b8";

// Mini SVG donut chart (sin dependencias externas) — muestra el peso de cada
// actividad para un operador en una celda de 44×44 px.
function MiniDonut({ byActivity = {}, activities = [], size = 40, title }) {
  const stroke = 6;
  const r = (size - stroke) / 2;
  const cx = size / 2, cy = size / 2;
  const entries = activities
    .map(a => ({ act: a, pct: Math.max(0, Number(byActivity[a] || 0)) }))
    .filter(e => e.pct > 0);
  const total = entries.reduce((s, e) => s + e.pct, 0);
  if (total === 0) return <span className="text-gray-300 text-[10px]">—</span>;
  // Convertir cada slice en arco SVG
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const arcs = entries.map((e, i) => {
    const frac = e.pct / total;
    const dash = frac * circ;
    const arc = (
      <circle key={i} cx={cx} cy={cy} r={r} fill="none"
              stroke={activityColor(e.act)} strokeWidth={stroke}
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${cx} ${cy})`}/>
    );
    offset += dash;
    return arc;
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
         data-testid="competition-mini-donut"
         className="inline-block align-middle"
         aria-label={title || "Distribución por actividad"}>
      <title>{entries.map(e => `${e.act}: ${e.pct}%`).join(" · ")}</title>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke}/>
      {arcs}
    </svg>
  );
}

const MODE_LABELS = {
  resolved:  { title: "Resuelto en el año",  hint: "Sólo adjudicaciones cerradas (por fecha de adjudicación). No incluye pendientes." },
  presented: { title: "Presentado en el año", hint: "Todas las ofertas presentadas ese año. Provisional mientras queden pendientes de adjudicar." },
  incumbent: { title: "Incumbents",           hint: "Operador titular actual del contrato (histórico)." },
};

export default function CompetitionMapView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("resolved"); // resolved | presented | incumbent
  // Iter195 · Ámbito: mercado total vs sólo ofertas VSM presentadas
  const [scope, setScope] = useState("all"); // all | vsm_presented
  const [year, setYear] = useState(null);
  // Iter178 · Ordenación por columna. sortKey = "pct" (default) o un actividad ("C&W", "GA"…)
  const [sortKey, setSortKey] = useState("pct");
  const [sortDir, setSortDir] = useState("desc");
  // Iter178 · Filtro por actividad (backend recomputa el ranking al filtrarse)
  const [actividad, setActividad] = useState("");
  // Iter179.4 · Filtro por DG (chips múltiples). Vacío = "Todas".
  const [dgSet, setDgSet] = useState(() => new Set());
  // Iter179.4 · Datos por actividad C&W / GA / MOB (bloques apilados)
  const [byActData, setByActData] = useState({}); // { "C&W": {...}, "GA": {...}, "MOB": {...} }

  const dgParam = useMemo(() => Array.from(dgSet).join(","), [dgSet]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const baseParams = { scope };
        if (actividad) baseParams.actividad = actividad;
        if (dgParam) baseParams.dg = dgParam;
        const r = await axios.get(`${API}/mercado/competition-map`, { params: baseParams });
        if (alive) setData(r.data);
      } catch (_) { /* silent */ } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [actividad, dgParam, scope]);

  // Iter179.4 · Cargar 3 datasets adicionales para los mini-bloques por actividad.
  useEffect(() => {
    let alive = true;
    (async () => {
      // R08 (auditoría externa) · antes solo cubría C&W/GA/MOB — O&M,
      // INDUSTRIAL y Facility Services quedaban fuera de este análisis
      // aunque son actividades reales con licitaciones trackeadas.
      const acts = ALL_ACTIVIDADES;
      const results = await Promise.all(acts.map(async (a) => {
        try {
          const params = { actividad: a, scope };
          if (dgParam) params.dg = dgParam;
          const r = await axios.get(`${API}/mercado/competition-map`, { params });
          return [a, r.data];
        } catch { return [a, null]; }
      }));
      if (alive) setByActData(Object.fromEntries(results));
    })();
    return () => { alive = false; };
  }, [dgParam, scope]);

  const toggleDg = (dg) => setDgSet(prev => {
    const next = new Set(prev);
    if (next.has(dg)) next.delete(dg); else next.add(dg);
    return next;
  });

  const dataset = useMemo(() => {
    if (!data) return null;
    if (mode === "resolved") return data.resolved_by_year || data.winner_by_year || {};
    if (mode === "presented") return data.presented_by_year || {};
    return data.incumbent_by_year || {};
  }, [data, mode]);

  const years = useMemo(() => {
    if (!dataset) return [];
    return Object.keys(dataset).filter(y => y !== "?" && y !== "").sort((a, b) => b.localeCompare(a));
  }, [dataset]);

  useEffect(() => {
    if (years.length && (!year || !years.includes(year))) setYear(years[0]);
  }, [years, year]);

  const rawYearData = year && dataset ? dataset[year] || [] : [];
  // Iter178 · Aplicar ordenación por columna manteniendo "Otros" y "Pendiente"
  // siempre al final (son filas resumen, no operadores).
  const currentYearData = useMemo(() => {
    if (rawYearData.length === 0) return rawYearData;
    const special = rawYearData.filter(r => r.norm === "__OTROS__" || r.norm === "__PENDIENTE__");
    const operators = rawYearData.filter(r => r.norm !== "__OTROS__" && r.norm !== "__PENDIENTE__");
    const getVal = (r) => sortKey === "pct"
      ? (r.pct || 0)
      : (r.by_actividad?.[sortKey] || 0);
    operators.sort((a, b) => sortDir === "desc" ? getVal(b) - getVal(a) : getVal(a) - getVal(b));
    return [...operators, ...special];
  }, [rawYearData, sortKey, sortDir]);
  const setSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortKey(key); setSortDir("desc"); }
  };
  const sortArrow = (key) => sortKey === key ? (sortDir === "desc" ? " ↓" : " ↑") : "";
  const provisionalMap = data?.provisional_by_year || {};
  const pendingMap = data?.pending_by_year || {};
  const yearTotalsMap = mode === "resolved"
    ? (data?.resolved_totals_by_year || {})
    : mode === "presented"
      ? (data?.presented_totals_by_year || {})
      : {};
  const yearTotal = yearTotalsMap[year] || currentYearData.reduce((s, x) => s + (x.count || 0), 0);
  const isProvisional = mode === "presented" && !!provisionalMap[year];
  const pendingCount = pendingMap[year] || 0;

  const provinceData = data?.winner_by_province || {};
  const provinces = Object.keys(provinceData).sort();

  // Timeline: para un operador dado, evolución % por año (por norm para que
  // no divergan las variantes de mayúsculas).
  const topOperators = useMemo(() => {
    if (!dataset) return [];
    const totals = {};
    const displayByNorm = {};
    Object.values(dataset).forEach(arr => {
      arr.forEach(({ name, norm, count }) => {
        if (norm === "__OTROS__" || norm === "__PENDIENTE__") return;
        const key = norm || name;
        totals[key] = (totals[key] || 0) + count;
        displayByNorm[key] = name;
      });
    });
    return Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([k]) => ({ norm: k, name: displayByNorm[k] }));
  }, [dataset]);

  const timelineData = useMemo(() => {
    if (!dataset || years.length === 0) return [];
    return years.slice().reverse().map(y => {
      const row = { year: y };
      const yr = dataset[y] || [];
      topOperators.forEach(op => {
        const found = yr.find(x => (x.norm || x.name) === op.norm);
        row[op.name] = found ? found.pct : 0;
      });
      return row;
    });
  }, [years, dataset, topOperators]);

  // Iter195 · Salvaguarda cliente: forzar que "C&W" aparezca siempre primero
  // en las columnas del ranking (aunque el backend cambie el orden).
  const activitiesForColumns = useMemo(() => {
    const all = data?.activities_available || [];
    const priority = ["C&W"];
    const first = priority.filter(a => all.includes(a));
    const rest = all.filter(a => !priority.includes(a));
    return [...first, ...rest];
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400" data-testid="competition-loading">
        <Loader2 className="animate-spin mr-2" size={16}/> Cargando competencia…
      </div>
    );
  }

  if (!data || years.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400" data-testid="competition-empty">
        <Trophy size={32} className="mx-auto mb-3 text-gray-300"/>
        <p className="text-sm">Aún no hay datos de winner/incumbent registrados.</p>
        <p className="text-xs mt-2">Rellena los campos Incumbent y Winner en las fichas MERCADO / Licitaciones para ver el análisis.</p>
      </div>
    );
  }

  const modeLabel = MODE_LABELS[mode];

  // Color por fila según norm (Otros/Pendiente tienen color fijo)
  const colorFor = (row, idx) => {
    if (row.norm === "__OTROS__") return OTROS_COLOR;
    if (row.norm === "__PENDIENTE__") return PENDIENTE_COLOR;
    return OPERATOR_COLORS[idx % OPERATOR_COLORS.length];
  };

  return (
    <div className="space-y-6" data-testid="competition-view">
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Trophy size={18} className="text-emerald-600"/>
            <div>
              <h2 className="text-sm font-bold text-gray-900">Análisis de Competencia</h2>
              <p className="text-[11px] text-gray-500 mt-0.5" data-testid="competition-mode-hint">{modeLabel.hint}</p>
              {/* Iter307 · D10 — la muestra es lo detectado por Valoriza, no el sector completo */}
              <p className="text-[10px] text-amber-700 mt-0.5 font-medium" data-testid="competition-sample-disclaimer">
                Muestra observada en Valoriza — no representa la cuota de todo el sector.
              </p>
              {/* C07 (CRM audit 2026-10-13) · declara el cambio de alcance: el
                  filtro DG global de arriba NO se aplica aquí; Competencia usa
                  su propio selector DG (derecha). */}
              <p className="text-[10px] text-slate-400 mt-0.5" data-testid="competition-dg-scope-note">
                Usa su propio selector de DG (no hereda el filtro global de la cabecera).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Iter195 · Toggle Ámbito: Mercado total vs Mis presentadas */}
            <div className="flex bg-gray-100 rounded-lg p-0.5" data-testid="competition-scope-toggle" title="Elige entre ver todo el mercado (mercado_opportunities + ofertas VSM) o solo tus ofertas presentadas por VSM.">
              <button
                onClick={() => setScope("all")}
                data-testid="competition-scope-all"
                className={`text-xs font-semibold px-3 py-1.5 rounded-md transition ${
                  scope === "all" ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500"
                }`}>Mercado observado</button>
              <button
                onClick={() => setScope("vsm_presented")}
                data-testid="competition-scope-vsm"
                className={`text-xs font-semibold px-3 py-1.5 rounded-md transition ${
                  scope === "vsm_presented" ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500"
                }`}>Mis presentadas</button>
            </div>
            <div className="flex bg-gray-100 rounded-lg p-0.5" data-testid="competition-mode-toggle">
              <button
                onClick={() => setMode("resolved")}
                data-testid="competition-mode-resolved"
                className={`text-xs font-semibold px-3 py-1.5 rounded-md transition ${
                  mode === "resolved" ? "bg-white text-emerald-700 shadow-sm" : "text-gray-500"
                }`}>Resuelto</button>
              <button
                onClick={() => setMode("presented")}
                data-testid="competition-mode-presented"
                className={`text-xs font-semibold px-3 py-1.5 rounded-md transition ${
                  mode === "presented" ? "bg-white text-emerald-700 shadow-sm" : "text-gray-500"
                }`}>Presentado</button>
              <button
                onClick={() => setMode("incumbent")}
                data-testid="competition-mode-incumbent"
                className={`text-xs font-semibold px-3 py-1.5 rounded-md transition ${
                  mode === "incumbent" ? "bg-white text-emerald-700 shadow-sm" : "text-gray-500"
                }`}>Incumbents</button>
            </div>
            {/* Iter179.4 · Chips DG multi-selección (Servicios + Tratamiento) */}
            <div className="inline-flex items-center gap-1 bg-gray-100 rounded-lg p-1"
                 data-testid="competition-dg-chips" title="Filtrar por Dirección General (Servicios / Tratamiento). Sin selección = todas.">
              <span className="text-[10px] text-gray-500 pl-1 pr-1 font-semibold uppercase tracking-wide">DG</span>
              {["SERVICIOS","TRATAMIENTO"].map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => toggleDg(v)}
                  data-testid={`competition-dg-${v.toLowerCase()}`}
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-md transition ${
                    dgSet.has(v)
                      ? (v === "SERVICIOS"
                          ? "bg-blue-600 text-white shadow-sm"
                          : "bg-amber-600 text-white shadow-sm")
                      : "text-gray-500 hover:text-gray-700"
                  }`}>
                  {v === "SERVICIOS" ? "Servicios" : "Tratamiento"}
                </button>
              ))}
              {dgSet.size > 0 && (
                <button
                  type="button"
                  onClick={() => setDgSet(new Set())}
                  data-testid="competition-dg-clear"
                  className="text-[10px] text-gray-400 hover:text-gray-600 px-1.5"
                  title="Quitar filtro DG">×</button>
              )}
            </div>
            {/* Iter178 · Filtro por actividad · recalcula el ranking en backend */}
            <select
              value={actividad}
              onChange={e => setActividad(e.target.value)}
              data-testid="competition-actividad-select"
              title="Filtrar el ranking por actividad comercial"
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
              <option value="">Todas las actividades</option>
              {(data?.activities_available || []).map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <select
              value={year || ""}
              onChange={e => setYear(e.target.value)}
              data-testid="competition-year-select"
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Ranking por año */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="text-xs font-bold uppercase tracking-wide text-emerald-800" data-testid="competition-year-title">
            {modeLabel.title} · {year}
          </h3>
          <div className="flex items-center gap-2">
            {yearTotal > 0 && (
              <span className="text-[11px] text-gray-500" data-testid="competition-year-total">
                Total: <b className="text-gray-800">{yearTotal}</b> ofertas
                {yearTotal < 5 && (
                  <span className="ml-1.5 text-amber-600 font-semibold" title="Muestra pequeña: el % de cada operador puede no ser representativo." data-testid="competition-small-sample">
                    · muestra pequeña
                  </span>
                )}
              </span>
            )}
            {mode === "presented" && (
              isProvisional ? (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                  data-testid="competition-provisional-badge"
                  title={`${pendingCount} pendientes de adjudicar en ${year}`}>
                  <AlertTriangle size={11}/> Provisional · {pendingCount} pendientes
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                  data-testid="competition-definitive-badge">
                  <CheckCircle2 size={11}/> Definitivo
                </span>
              )
            )}
          </div>
        </div>
        {currentYearData.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Sin datos en este año.</p>
        ) : (
          <>
            {/* Iter177 · Tabla ranking con % Total + desglose por actividad
                (C&W / GA / MOB) — solo en modos Resuelto/Presentado. */}
            {(mode === "resolved" || mode === "presented") && (data?.activities_available?.length > 0) ? (
              <div className="overflow-x-auto" data-testid={`competition-table-${mode}`}>
                {/* Iter178 · Pill de filtro activo */}
                {actividad && (
                  <div className="mb-2 flex items-center gap-2 text-[11px]">
                    <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-2 py-0.5 font-semibold"
                          data-testid="competition-actividad-pill">
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: activityColor(actividad) }}/>
                      Filtro: {actividad}
                    </span>
                    <button onClick={() => setActividad("")}
                            data-testid="competition-actividad-clear"
                            className="text-gray-500 hover:text-gray-700 underline">Quitar filtro</button>
                  </div>
                )}
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-[10px] uppercase tracking-wide text-gray-600">
                      <th className="px-3 py-2 w-8">#</th>
                      <th className="px-3 py-2">Operador</th>
                      <th className="px-3 py-2 text-right">Nº</th>
                      <th className="px-3 py-2 text-right cursor-pointer select-none hover:text-emerald-700"
                          onClick={() => setSort("pct")}
                          data-testid="competition-sort-pct"
                          title="Ordenar por % total">
                        % {actividad ? actividad : "Total"}{sortArrow("pct")}
                      </th>
                      {!actividad && (activitiesForColumns || []).slice(0, 6).map(act => (
                        <th key={act}
                            className="px-3 py-2 text-right whitespace-nowrap cursor-pointer select-none hover:text-emerald-700"
                            onClick={() => setSort(act)}
                            data-testid={`competition-sort-${act.replace(/\W+/g, '_')}`}
                            title={`Ordenar por % ${act}`}>
                          % {act}{sortArrow(act)}
                        </th>
                      ))}
                      {!actividad && (
                        <th className="px-3 py-2 text-center w-14" title="Reparto por actividad de cada operador">Mix</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {currentYearData.map((row, i) => {
                      const by = row.by_actividad || {};
                      const isSpecial = row.norm === "__OTROS__" || row.norm === "__PENDIENTE__";
                      return (
                        <tr key={`${row.norm}-${i}`}
                            className={`border-t border-gray-100 hover:bg-gray-50 ${isSpecial ? "italic text-gray-500" : ""}`}
                            data-testid={`competition-row-${mode}-${i}`}>
                          <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                          <td className="px-3 py-2 font-semibold">
                            <span
                              className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                              style={{ background: colorFor(row, i) }}/>
                            {row.name}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{row.count}</td>
                          <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">
                            {row.pct}%
                          </td>
                          {!actividad && (activitiesForColumns || []).slice(0, 6).map(act => (
                            <td key={act} className={`px-3 py-2 text-right font-mono ${
                              sortKey === act ? "bg-emerald-50/60 text-emerald-800 font-bold" : "text-gray-600"
                            }`}>
                              {by[act] > 0 ? `${by[act]}%` : "—"}
                            </td>
                          ))}
                          {!actividad && (
                            <td className="px-3 py-2 text-center">
                              <MiniDonut
                                byActivity={by}
                                activities={data.activities_available || []}
                                title={`${row.name}: ${(data.activities_available || []).map(a => `${a} ${by[a]||0}%`).join(" · ")}`}
                              />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* Iter178 · Leyenda compacta de actividades (colores del donut) */}
                {!actividad && (
                  <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-100" data-testid="competition-activity-legend">
                    {(data.activities_available || []).slice(0, 8).map(act => (
                      <span key={act} className="inline-flex items-center gap-1.5 text-[10px] text-gray-600">
                        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: activityColor(act) }}/>
                        {act}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={currentYearData} layout="vertical" margin={{ left: 40, right: 40, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                  <XAxis type="number" tick={{ fontSize: 10 }}/>
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={160}/>
                  <Tooltip
                    formatter={(val, key, ctx) => {
                      if (key === "count") {
                        const pct = ctx?.payload?.pct;
                        return [`${val} ofertas · ${pct}%`, "Nº"];
                      }
                      return [`${val}%`, "%"];
                    }}/>
                  <Bar dataKey="count" data-testid={`competition-bar-${mode}`}>
                    {currentYearData.map((row, i) => (
                      <Cell key={i} fill={colorFor(row, i)}/>
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </>
        )}
        {/* Base + nota de redondeo honesta (V06). Los porcentajes son
            count/base exactos; pueden no sumar 100 por redondeo y NO se
            nudgea ningún operador para forzar el 100. */}
        {currentYearData.length > 0 && (
          <div className="mt-2 text-[10px] text-gray-400 text-right" data-testid="competition-sum-check">
            {(() => {
              const suma = currentYearData.reduce((s, x) => s + (x.pct || 0), 0);
              const residuo = Math.abs(100 - suma);
              return (
                <span title={`Porcentajes calculados como nº de adjudicaciones de cada operador ÷ ${yearTotal || "base"} (total del año). No se ajusta ninguna tasa para cuadrar el 100%.`}>
                  Porcentajes sobre <b className="text-gray-600">{yearTotal || "—"}</b> · suma {suma.toFixed(1)}%
                  {residuo >= 0.1 && <span> (no llega a 100 por redondeo)</span>}
                </span>
              );
            })()}
          </div>
        )}
      </div>

      {/* Timeline % top 5 operadores */}
      {topOperators.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={14} className="text-emerald-600"/>
            <h3 className="text-xs font-bold uppercase tracking-wide text-emerald-800">Evolución % · Top 5 operadores ({modeLabel.title})</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={timelineData} margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
              <XAxis dataKey="year" tick={{ fontSize: 10 }}/>
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`}/>
              <Tooltip formatter={val => `${val}%`}/>
              <Legend wrapperStyle={{ fontSize: 10 }}/>
              {topOperators.map((op, i) => (
                <Bar key={op.norm} dataKey={op.name} fill={OPERATOR_COLORS[i % OPERATOR_COLORS.length]}/>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Distribución geográfica */}
      {provinces.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={14} className="text-emerald-600"/>
            <h3 className="text-xs font-bold uppercase tracking-wide text-emerald-800">Ganador dominante por provincia (resuelto)</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="competition-provinces-grid">
            {provinces.map(prov => {
              const top = provinceData[prov][0];
              if (!top) return null;
              return (
                <div key={prov} className="border border-gray-100 rounded-lg p-3 hover:border-emerald-300 transition"
                     data-testid={`competition-province-${prov.replace(/\W+/g, '_')}`}>
                  <div className="text-[10px] uppercase text-gray-500 font-semibold">{prov}</div>
                  <div className="text-sm font-bold text-gray-900 mt-1 truncate" title={top.name}>{top.name}</div>
                  <div className="text-[10px] text-emerald-600 font-mono">{top.pct}% · {top.count} ofertas</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Iter179.4 · Análisis mercado a mercado (C&W / GA / MOB) — apilados */}
      <div className="space-y-3" data-testid="competition-by-actividad">
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-bold text-emerald-900">Análisis mercado a mercado</h3>
          <span className="text-[11px] text-gray-500">
            Un bloque por actividad · {dgSet.size === 0 ? "todas las DG" : `filtro: ${Array.from(dgSet).map(v => v === "SERVICIOS" ? "Servicios" : "Tratamiento").join(" + ")}`}
          </span>
        </div>
        {ALL_ACTIVIDADES.map(act => (
          <ActividadMiniBlock
            key={act}
            actividad={act}
            data={byActData[act]}
            mode={mode}
            year={year}
          />
        ))}
      </div>
    </div>
  );
}

// Iter179.4 · Bloque compacto por actividad · Top operadores + total del año
const _MINI_COLORS = {
  "C&W": { border: "border-emerald-200", dot: "bg-emerald-500", text: "text-emerald-800", bar: "bg-emerald-500" },
  "GA":  { border: "border-sky-200",     dot: "bg-sky-500",     text: "text-sky-800",     bar: "bg-sky-500" },
  "MOB": { border: "border-violet-200",  dot: "bg-violet-500",  text: "text-violet-800",  bar: "bg-violet-500" },
};
function ActividadMiniBlock({ actividad, data, mode, year }) {
  const c = _MINI_COLORS[actividad] || _MINI_COLORS["C&W"];
  const dataset = mode === "resolved"
    ? (data?.resolved_by_year || {})
    : mode === "presented"
      ? (data?.presented_by_year || {})
      : (data?.incumbent_by_year || {});
  const yearsAvail = Object.keys(dataset).filter(y => y !== "?" && y !== "").sort((a, b) => b.localeCompare(a));
  const actYear = year && dataset[year] ? year : yearsAvail[0] || null;
  const rows = actYear ? (dataset[actYear] || []).filter(r => r.norm !== "__OTROS__" && r.norm !== "__PENDIENTE__") : [];
  const top5 = rows.slice(0, 5);
  const totalsMap = mode === "resolved"
    ? (data?.resolved_totals_by_year || {})
    : mode === "presented"
      ? (data?.presented_totals_by_year || {})
      : {};
  const total = actYear ? (totalsMap[actYear] || rows.reduce((s, r) => s + (r.count || 0), 0)) : 0;

  if (!data || total === 0) {
    return (
      <div className={`bg-white rounded-xl border ${c.border} p-4 opacity-60`}
           data-testid={`competition-mini-${actividad.replace(/\W+/g,'_')}`}>
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${c.dot}`}/>
          <h4 className="text-xs font-bold uppercase tracking-wide text-gray-700">{actividad}</h4>
          <span className="text-[10px] text-gray-400">Sin datos con los filtros actuales</span>
        </div>
      </div>
    );
  }
  return (
    <div className={`bg-white rounded-xl border ${c.border} p-4`}
         data-testid={`competition-mini-${actividad.replace(/\W+/g,'_')}`}>
      <div className="flex items-baseline gap-2 mb-2 flex-wrap">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${c.dot}`}/>
        <h4 className={`text-xs font-bold uppercase tracking-wide ${c.text}`}>{actividad}</h4>
        <span className="text-[11px] text-gray-500" data-testid={`competition-mini-${actividad.replace(/\W+/g,'_')}-total`}>
          {actYear} · Total <b className="text-gray-800">{total}</b> ofertas
        </span>
      </div>
      <div className="space-y-1">
        {top5.map(r => (
          <div key={r.norm || r.name} className="flex items-center gap-2 text-[12px]"
               data-testid={`competition-mini-${actividad.replace(/\W+/g,'_')}-row-${(r.norm || r.name).replace(/\W+/g,'_')}`}>
            <span className="w-40 truncate font-medium text-gray-800" title={r.name}>{r.name}</span>
            <div className="flex-1 h-2 bg-gray-100 rounded overflow-hidden">
              <div className={`h-full ${c.bar}`} style={{ width: `${Math.min(100, r.pct || 0)}%` }}/>
            </div>
            <span className="w-16 text-right font-mono text-[11px] text-gray-600">{r.pct}%</span>
            <span className="w-14 text-right font-mono text-[11px] text-gray-500">{r.count}</span>
          </div>
        ))}
        {rows.length > 5 && (
          <div className="text-[10px] text-gray-400 pt-1 pl-2">+ {rows.length - 5} operadores más</div>
        )}
      </div>
    </div>
  );
}
